import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listAllSubjects } from "../services/subject";
import { listFrequencies } from "../services/teacher";
import { levelKey } from "../utils/classesLabels";

const PAGE_SIZE = 8;

// Split out of the former DisciplinasLayer (which mixed the wizard with the
// subject table). Mirrors ListaProdutosLayer: the create screen is hidden
// from the menu and only reachable through the "Adicionar Disciplina"
// button here — see CadastrarDisciplinaLayer / App.js route
// `/academico/disciplinas/cadastrar`.
const ListaDisciplinasLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const fetchSubjects = () => {
    setLoading(true);
    listAllSubjects()
      .then((resp) => setSubjects(resp.data || []))
      .catch(() => toast.error(t("disciplina.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
    fetchSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const frequencyName = (id) => frequencies.find((f) => f.id === id)?.description || "-";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return subjects.filter((s) => {
      const matchesTerm =
        !term ||
        s.subjectName?.toLowerCase().includes(term) ||
        s.code?.toLowerCase().includes(term) ||
        s.abbreviation?.toLowerCase().includes(term);
      const matchesLevel = !levelFilter || String(s.levelId) === String(levelFilter);
      return matchesTerm && matchesLevel;
    });
  }, [subjects, search, levelFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setLevelFilter("");
    setPage(1);
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const curricularCount = subjects.filter((s) => s.isCurricular).length;
  const dispensaCount = subjects.filter((s) => s.hasDispensa).length;

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:book-open-variant-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{subjects.length}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("disciplina.list.stat.total")}
                </span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-success-focus text-success-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='ic:outline-check-circle' />
              </span>
              <div>
                <h6 className='mb-0'>{curricularCount}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("disciplina.list.stat.curricular")}
                </span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-info-focus text-info-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:file-document-check-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{dispensaCount}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("disciplina.list.stat.dispensa")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("disciplina.section.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("disciplina.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/academico/disciplinas/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("disciplina.list.add")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-5'>
                  <label className='form-label text-sm mb-8'>{t("disciplina.field.subjectName")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("disciplina.list.search.placeholder")}
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("disciplina.field.level"))}</label>
                  <select
                    className='form-control form-select'
                    value={levelFilter}
                    onChange={(e) => {
                      setLevelFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t(levelKey("disciplina.filter.allLevels"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("disciplina.field.subjectName")}</th>
                    <th scope='col'>{t("disciplina.field.code")}</th>
                    <th scope='col'>{t(levelKey("disciplina.field.level"))}</th>
                    <th scope='col'>{t("disciplina.field.isCurricular")}</th>
                    <th scope='col'>{t("disciplina.field.totalCredits")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("global.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("disciplina.hint.noSubjects")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((subject) => {
                    const isExpanded = expandedId === subject.id;
                    return (
                      <React.Fragment key={subject.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(subject.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{subject.subjectName}</td>
                          <td>{subject.code || "-"}</td>
                          <td>{frequencyName(subject.levelId)}</td>
                          <td>
                            <span
                              className={
                                subject.isCurricular
                                  ? "badge bg-success-focus text-success-main"
                                  : "badge bg-neutral-200 text-neutral-600"
                              }
                            >
                              {subject.isCurricular ? t("global.yes") : t("global.no")}
                            </span>
                          </td>
                          <td>{subject.totalCredits || "-"}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-12'>
                                    <table className='table table-bordered table-sm mb-0 bg-base'>
                                      <tbody>
                                        <tr>
                                          <th className='w-25 bg-neutral-50'>
                                            {t("disciplina.field.subjectName")}
                                          </th>
                                          <td colSpan={3}>
                                            <strong>{subject.subjectName}</strong>
                                          </td>
                                        </tr>
                                        <tr>
                                          <th className='bg-neutral-50'>{t("disciplina.field.code")}</th>
                                          <td>{subject.code || "-"}</td>
                                          <th className='bg-neutral-50'>
                                            {t("disciplina.field.abbreviation")}
                                          </th>
                                          <td>{subject.abbreviation || "-"}</td>
                                        </tr>
                                        <tr>
                                          <th className='bg-neutral-50'>
                                            {t("disciplina.field.totalCredits")}
                                          </th>
                                          <td>{subject.totalCredits || "-"}</td>
                                          <th className='bg-neutral-50'>
                                            {t("disciplina.field.isCurricular")}
                                          </th>
                                          <td>{subject.isCurricular ? t("global.yes") : t("global.no")}</td>
                                        </tr>
                                        <tr>
                                          <th className='bg-neutral-50'>
                                            {t("disciplina.field.hasDispensa")}
                                          </th>
                                          <td colSpan={3}>
                                            {subject.hasDispensa ? t("global.yes") : t("global.no")}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("disciplina.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaDisciplinasLayer;
